import * as Yup from "yup";
import moment from "moment";
import { ICreateObjectRide } from "app/data/types/objectRideTypes";
import { ObjectRideModeEnum } from "./objectRideHelpers";

export interface ICreateManualRideFormValues {
  startDate: string;
  endDate: string;
  startAddress: string;
  startLatitude: number | null;
  startLongitude: number | null;
  endAddress: string;
  endLatitude: number | null;
  endLongitude: number | null;
  rideMode: number | "";
  mileage: number | "";
  privateMileage: number | "" | null;
  driverId: string;
  keyId: string;
  comments: string;
}

export const CREATE_OBJECT_RIDE_MODE_OPTIONS = (
  translator: (key: string) => string
) => [
  {
    value: ObjectRideModeEnum.BUSINESS,
    label: translator("objectRideModes.1")
  },
  {
    value: ObjectRideModeEnum.PRIVATE,
    label: translator("objectRideModes.2")
  },
  {
    value: ObjectRideModeEnum.COMMUTE,
    label: translator("objectRideModes.3")
  },
  {
    value: ObjectRideModeEnum.MIXED,
    label: translator("objectRideModes.4")
  }
];

export const createManualRideInitialValues: ICreateManualRideFormValues = {
  startDate: "",
  endDate: "",
  startAddress: "",
  startLatitude: null,
  startLongitude: null,
  endAddress: "",
  endLatitude: null,
  endLongitude: null,
  rideMode: "",
  mileage: "",
  privateMileage: "",
  driverId: "",
  keyId: "",
  comments: ""
};

const isNotInFuture = (value?: string | null) => {
  if (!value) return true;
  return !moment(value).isAfter(moment());
};

export const createManualRideValidationSchema = (t: (key: string) => string) =>
  Yup.object({
    startDate: Yup.string()
      .required(t("validation.startDateRequired"))
      .test(
        "not-in-future",
        t("validation.startDateCannotBeInTheFuture"),
        isNotInFuture
      ),
    endDate: Yup.string()
      .required(t("validation.endDateRequired"))
      .test(
        "not-in-future",
        t("validation.endDateCannotBeInTheFuture"),
        isNotInFuture
      )
      .test(
        "after-start",
        t("validation.endDateMustBeAfterStartDate"),
        function (value) {
          const { startDate } = this.parent;
          if (!value || !startDate) return true;
          return moment(value).isAfter(moment(startDate));
        }
      ),
    startAddress: Yup.string().required(t("validation.startAddressRequired")),
    startLatitude: Yup.number()
      .nullable()
      .required(t("validation.startAddressRequired")),
    startLongitude: Yup.number()
      .nullable()
      .required(t("validation.startAddressRequired")),
    endAddress: Yup.string().required(t("validation.endAddressRequired")),
    endLatitude: Yup.number()
      .nullable()
      .required(t("validation.endAddressRequired")),
    endLongitude: Yup.number()
      .nullable()
      .required(t("validation.endAddressRequired")),
    rideMode: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue == null
          ? undefined
          : Number(originalValue)
      )
      .oneOf(
        [
          ObjectRideModeEnum.BUSINESS,
          ObjectRideModeEnum.PRIVATE,
          ObjectRideModeEnum.COMMUTE,
          ObjectRideModeEnum.MIXED
        ],
        t("validation.rideModeRequired")
      )
      .required(t("validation.rideModeRequired")),
    mileage: Yup.number()
      .typeError(t("validation.mustBeNumber"))
      .moreThan(0, t("validation.mileageMustBeGreaterThanZero"))
      .required(t("validation.mileageRequired")),
    privateMileage: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue == null ? null : value
      )
      .when("rideMode", {
        is: (rideMode: number | string) =>
          Number(rideMode) === ObjectRideModeEnum.MIXED,
        then: (schema) =>
          schema
            .typeError(t("validation.mustBeNumber"))
            .required(t("validation.privateMileageRequired"))
            .moreThan(0, t("validation.privateMileageMustBeGreaterThanZero"))
            .test(
              "lte-mileage",
              t("validation.privateMileageCannotExceedMileage"),
              function (value) {
                const { mileage } = this.parent;
                if (value == null || mileage == null || mileage === "") {
                  return true;
                }
                return Number(value) <= Number(mileage);
              }
            ),
        otherwise: (schema) => schema.nullable().notRequired()
      }),
    driverId: Yup.string().nullable(),
    keyId: Yup.string().nullable(),
    comments: Yup.string().nullable()
  }).test("at-least-one-driver-or-key", function (values) {
    const hasDriver = !!values?.driverId;
    const hasKey = !!values?.keyId;
    // Allow both transiently: FormikSelectField validates before our XOR clear runs.
    // UI + transformCreateManualRideForApi enforce a single selection.
    if (hasDriver || hasKey) {
      return true;
    }

    return this.createError({
      path: "driverId",
      message: t("validation.eitherDriverOrKey")
    });
  });

export const transformCreateManualRideForApi = (
  values: ICreateManualRideFormValues
): ICreateObjectRide => {
  const rideMode = Number(values.rideMode);
  const isMixed = rideMode === ObjectRideModeEnum.MIXED;

  return {
    startDate: moment(values.startDate).utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
    endDate: moment(values.endDate).utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
    startAddress: values.startAddress,
    startLatitude: Number(values.startLatitude),
    startLongitude: Number(values.startLongitude),
    endAddress: values.endAddress,
    endLatitude: Number(values.endLatitude),
    endLongitude: Number(values.endLongitude),
    rideMode,
    mileage: Number(values.mileage),
    privateMileage:
      isMixed && values.privateMileage !== "" && values.privateMileage != null
        ? Number(values.privateMileage)
        : null,
    driverId: values.keyId ? null : values.driverId ? values.driverId : null,
    keyId: values.keyId ? values.keyId : null,
    comments: values.comments?.trim() ? values.comments.trim() : null
  };
};
